// ExamSetForm: Form for creating and editing Exam Sets
import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { GenericFormProps } from "../../Utils/Types";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { ExamSetModel, ExamSetValidationSchema } from "../../Utils/Models";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import { updateData } from "../../Services/updateData";
import { onFormError } from "../../Utils/Utils";
import { FormsIds } from "../../Utils/Constants";
import InfoNotice from "../InfoNotice";

// Props for ExamSetForm: extends GenericFormProps and adds filteredProducts
interface ExamSetFormProps extends GenericFormProps<ExamSetModel> {
  examCategories?: string[];
  products?: any[];
}

// Main functional component
export default function ExamSetForm(props: ExamSetFormProps) {
  const { onClose, selectedRow, examCategories, products = [] } = props;

  // Setup react-hook-form with Yup validation
  const { control, handleSubmit, reset } = useForm<any>({
    resolver: yupResolver(ExamSetValidationSchema),
    defaultValues: selectedRow || {},
  });

  /**
   *  Reset form when selectedRow changes
   * This ensures that the form is populated with the selected row data when editing.
   *
   *  */
  useEffect(() => {
    reset(selectedRow || {});
  }, [selectedRow, reset]);

  /**
   * Helper to format data for FormData (for file/image upload)
   * This function converts the form data into a FormData object,
   **/
  const formatData = (model: any) => {
    const formData = new FormData();
    Object.keys(model).forEach((key) => {
      const value = model[key];
      if (Array.isArray(value) && value[value.length - 1] instanceof File) {
        value.forEach((file) => {
          formData.append(key, file);
        });
      } else if (Array.isArray(value) && value.length > 0) {
        formData.append(key, JSON.stringify(value.map((v: any) => v._id || v)));
      } else {
        formData.append(key, value);
      }
    });
    return formData;
  };

  // Create Exam Set API call
  const createElement = async (data: ExamSetModel) => {
    try {
      const formData = formatData(data);
      const url = `${endpoints.examSets}`;
      const response = await postData(url, formData);
      if (!response) {
        return;
      }
      if (onClose) onClose();
    } catch (error) {
      console.error(error);
    }
  };

  // Update Exam Set API call
  const updateElement = async (data: ExamSetModel) => {
    try {
      const formData = formatData(data);
      const url = `${endpoints.examSets}/${data._id}`;
      const response = await updateData(url, formData);
      if (!response) {
        return;
      }
      if (onClose) onClose();
    } catch (error) {
      console.error(error);
    }
  };

  // Form submit handler: decides create or update
  const onSubmit = async (data: ExamSetModel) => {
    data?._id ? updateElement(data) : createElement(data);
  };

  // Render the form
  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      id={FormsIds.examSetForm}
    >
      <Grid container spacing={2}>
        {/* Name field */}
        <Grid item xs={12} sm={6} md={12}>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                label={Vocabulary.name}
                fullWidth
                margin="normal"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        {/* Exam Category Autocomplete field */}
        <Grid item xs={12} sm={6} md={12}>
          <Controller
            name={`examCategory`}
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                id={`examCategory`}
                fullWidth={true}
                freeSolo={false}
                disablePortal
                isOptionEqualToValue={(option: any, value: any) => {
                  return option == value;
                }}
                getOptionLabel={(option: any) => option || ""}
                options={Object.keys(examCategories ?? []) ?? []}
                value={field.value || null}
                onChange={(_, newValue) => field.onChange(newValue || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth={true}
                    name={`product.name`}
                    error={!!error}
                    helperText={error?.message}
                    label={Vocabulary.examCategory}
                  />
                )}
              />
            )}
          />
        </Grid>
        {/* Products Autocomplete field */}
        <Grid item xs={12} sm={6} md={12}>
          <Controller
            name="productsIds"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                multiple
                fullWidth
                options={products || []}
                getOptionLabel={(option) => option.name || ""}
                value={field.value || []}
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Vocabulary.products}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={12}>
          <Controller
            name="questionsInExam"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                label={Vocabulary.questionsInExam}
                fullWidth
                margin="normal"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        {/* Info Notice */}
        <Grid item xs={12}>
          <InfoNotice message={Vocabulary.chooseCategory} />
        </Grid>
      </Grid>
    </form>
  );
}
